import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingDown, TrendingUp } from 'lucide-react';
import type { DashboardMarketPrice } from '@/api/trading.service';

export interface LiveBTCPriceProps {
    priceData?: DashboardMarketPrice | null;
    connectionStatus?: 'connected' | 'error' | 'loading';
    errorCount?: number;
    /** Label for the displayed pair, e.g. "ETH/USDT". Defaults to the BTC/USDT i18n string. */
    symbolLabel?: string;
}

export function LiveBTCPrice({
    priceData = null,
    connectionStatus = 'loading',
    errorCount = 0,
    symbolLabel,
}: LiveBTCPriceProps) {
    const [priceChange, setPriceChange] = useState<'up' | 'down' | 'same'>('same');
    const lastPriceRef = useRef<number | null>(null);
    const { t } = useTranslation();

    const displayPrice = Number(
        priceData?.mark_price ?? priceData?.markPrice ?? priceData?.price ?? NaN,
    );
    const hasPrice = Number.isFinite(displayPrice) && displayPrice > 0;

    useEffect(() => {
        if (!hasPrice) {
            return;
        }

        const previousPrice = lastPriceRef.current;
        if (previousPrice !== null && displayPrice !== previousPrice) {
            if (displayPrice > previousPrice) {
                setPriceChange('up');
            } else if (displayPrice < previousPrice) {
                setPriceChange('down');
            } else {
                setPriceChange('same');
            }

            const timeoutId = window.setTimeout(() => setPriceChange('same'), 1500);
            lastPriceRef.current = displayPrice;
            return () => window.clearTimeout(timeoutId);
        }

        lastPriceRef.current = displayPrice;
        return undefined;
    }, [displayPrice, hasPrice]);

    if (!hasPrice) {
        return (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <span>
                    {connectionStatus === 'loading'
                        ? t('dashboard.btcPrice.loading')
                        : t('dashboard.btcPrice.unavailable')}
                </span>
            </div>
        );
    }

    const changePercent = Number(priceData?.change_percent_24h ?? 0);
    const isPositive = changePercent >= 0;
    const isStale = Boolean(priceData?.is_stale);
    const staleAgeSec = Number(priceData?.stale_age_ms ?? NaN);
    const staleLabel =
        Number.isFinite(staleAgeSec) && staleAgeSec >= 0
            ? `STALE ${(staleAgeSec / 1000).toFixed(0)}s`
            : 'STALE';

    const getPriceChangeColor = () => {
        switch (priceChange) {
            case 'up':
                return 'text-green-600 bg-green-50 dark:bg-green-900/20';
            case 'down':
                return 'text-red-600 bg-red-50 dark:bg-red-900/20';
            default:
                return '';
        }
    };

    return (
        <div className="flex items-center space-x-3 text-sm">
            <span className="text-muted-foreground font-medium">
                {symbolLabel ?? t('dashboard.btcPrice.symbol')}:
            </span>
            <div
                className={`flex items-center space-x-2 px-2 py-1 rounded-md transition-all duration-500 ${getPriceChangeColor()}`}
            >
                <span className="font-mono font-bold text-base" data-btc-price={displayPrice}>
                    $
                    {displayPrice.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}
                </span>
                {priceChange !== 'same' && (
                    <div className="flex items-center">
                        {priceChange === 'up' ? (
                            <TrendingUp size={12} className="text-green-600 animate-pulse" />
                        ) : (
                            <TrendingDown size={12} className="text-red-600 animate-pulse" />
                        )}
                    </div>
                )}
            </div>
            {changePercent !== 0 && (
                <div
                    className={`flex items-center space-x-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}
                >
                    {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    <span className="font-mono text-xs">
                        {isPositive ? '+' : ''}
                        {changePercent.toFixed(2)}%
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {t('dashboard.btcPrice.24h')}
                    </span>
                </div>
            )}
            {isStale && (
                <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                    title="Price is older than the 5s publisher interval"
                >
                    {staleLabel}
                </span>
            )}
            <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <div
                    className={`w-2 h-2 rounded-full ${
                        connectionStatus === 'loading'
                            ? 'bg-yellow-400 animate-pulse'
                            : connectionStatus === 'connected'
                              ? 'bg-green-400'
                              : 'bg-red-400 animate-pulse'
                    }`}
                />
                <span>
                    {connectionStatus === 'loading'
                        ? t('dashboard.btcPrice.connecting')
                        : connectionStatus === 'connected'
                          ? t('dashboard.btcPrice.live')
                          : t('dashboard.btcPrice.error', { count: errorCount })}
                </span>
            </div>
        </div>
    );
}

export default LiveBTCPrice;
